import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment.prod";

declare let google: any;

@Injectable({
  providedIn: "root"
})
export class GoogleMapsAPIService {
  private autocomplete: any;
  private placesService: any;
  private sessionToken: any;
  private readonly themeLang = localStorage.getItem("theme_lang") ?? environment.defaultLang;

  async createAutocompleteInstance(): Promise<void> {
    if (!this.autocomplete) {
      const { AutocompleteSuggestion } = await google.maps.importLibrary("places");
      this.autocomplete = AutocompleteSuggestion;
    }
  }
  createPlacesServiceInstance(): void {
    if (!this.placesService) {
      this.placesService = new google.maps.places.PlacesService(document.createElement("div"));
    }
  }

  async getPlacePredictions(input: string, params = {}): Promise<any> {
    try {
      if (!input) return [];
      // Ensure the autocomplete instance is created
      await this.createAutocompleteInstance();
      // Initialize session token if not already done
      if (!this.sessionToken) {
        const { AutocompleteSessionToken } = await google.maps.importLibrary("places");
        this.sessionToken = new AutocompleteSessionToken(); // Ensure to call the constructor
      }

      // Check if the autocomplete service is available

      if (!this.autocomplete) {
        throw new Error("Autocomplete service is not initialized");
      }

      // Use async/await instead of .then() and .catch()
      const { suggestions } = await this.autocomplete.fetchAutocompleteSuggestions({
        input: input,
        sessionToken: this.sessionToken,
        language: this.themeLang,
        ...params
      });

      for (const suggestion of suggestions) {
        const placePrediction = suggestion.placePrediction;
        const text = placePrediction!.text.toString();
        const placeId = placePrediction!.placeId;
        suggestion["description"] = text;
        suggestion["place_id"] = placeId;
      }
      return suggestions;
    } catch (error) {
      // Handle any errors in the try block
      console.error("Error in getPlacePredictions:", error);
      throw error; // Rethrow or handle the error as needed
    }
  }

  getPlaceDetails(placeId: string): Observable<any> {
    this.createPlacesServiceInstance();
    return new Observable((observer) => {
      const request = {
        placeId: placeId,
        fields: ["name", "geometry", "address_components", "formatted_address", "types"],
        sessionToken: this.sessionToken
      };

      this.placesService.getDetails(request, (place: any, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          observer.next(place);
          this.sessionToken = undefined; // Clear session token after use
        } else {
          observer.error(status);
        }
        observer.complete();
      });
    });
  }

  async fetchFields(prediction: any): Promise<any> {
    const place = prediction.placePrediction.toPlace();

    await place.fetchFields({
      fields: ["displayName", "formattedAddress", "location", "addressComponents", "types"]
    });

    place["geometry"] = {
      location: place.location // Ensuring geometry.location remains accessible
    };

    place["formatted_address"] = place.formattedAddress;
    place["address_components"] = place.addressComponents;

    place.address_components.forEach((component: any) => {
      component["long_name"] = component["longText"].trim();
      component["short_name"] = component["shortText"].trim();
    });

    return place;
  }
}
